<script setup lang="ts">
import {cx} from 'class-variance-authority'
import {useOverflow} from '~/composables/cms/overflow'
import {resolveMediaUrl, resolveMediaAlt, resolveMediaProps} from '~/utils/relations'
import ByteCard from '~/components/overflow/byte-card.vue'
import FiltersTab from '~/components/overflow/filters-tab.vue'

const route = useRoute()

definePageMeta({
  key: route => route.path,
})

const {
  public: {baseUrl},
} = useRuntimeConfig()

const {fetchHero, fetchBytes} = useOverflow()

const activeFilter = ref<'recent' | 'liked'>(route.query.sort === 'liked' ? 'liked' : 'recent')
watch(activeFilter, () => {
  navigateTo({query: {sort: activeFilter.value}})
})

const {data: global} = await useLazyAsyncData('bytes:global', fetchHero)
const {data: bytes, pending} = await useLazyAsyncData(
  () => `bytes:${activeFilter.value}`,
  () => fetchBytes(activeFilter.value)
)

const hero = computed(() => global.value?.hero)

const seo = global.value?.meta

useSeoMeta({
  title: seo?.title || 'Overflow | Webamboos',
  description: seo?.description,
  ogUrl: `${baseUrl}/technical-bytes`,
  ogTitle: seo?.ogTitle || seo?.title,
  ogDescription: seo?.ogDescription || seo?.description,
  ogImage: resolveMediaUrl(seo?.image),
  ogImageAlt: resolveMediaAlt(seo?.image),
})

const gradient = 'text-transparent bg-clip-text bg-gradient-to-b'
</script>

<template>
  <Container class="flex min-h-screen flex-col items-center px-2 pt-16 sm:px-8 sm:pt-24 lg:px-8 lg:pt-32">
    <NuxtImg
      v-if="hero && hero.illustration"
      class="aspect-[3/1] h-full w-full max-w-3xl object-contain max-sm:min-w-[120%]"
      v-bind="resolveMediaProps(hero.illustration)"
      :width="800"
      :height="200"
    />

    <div class="relative z-10 -mt-8 mb-24 sm:-mt-16">
      <Heading size="hero" as="h1" class="text-center">
        <span :class="cx(gradient, 'from-main-400 to-main-600 pr-2')">
          {{ $t('overflow.title.top') }}
        </span>
        <br />
        <span class="pr-1" :class="cx(gradient, 'from-[#4D4D4D] to-[#9E9E9E]')">
          {{ $t('overflow.title.bottom') }}
        </span>
      </Heading>

      <Paragraph class="mx-auto mt-4 flex max-w-4xl px-4 text-center sm:px-8 lg:px-16 lg:text-lg" as="p" size="medium">
        {{ hero?.body }}
      </Paragraph>
    </div>

    <section class="w-full max-w-4xl pb-24">
      <FiltersTab v-model="activeFilter" class="mx-4 md:mx-8" />

      <div v-if="!pending && bytes" class="2xl:mx-auto 2xl:max-w-screen-2xl w-full space-y-2 md:px-4">
        <ByteCard v-for="(card, index) in bytes" :key="card.id" :byte="card" :style="{'--delay': `${index * 50}ms`}" />
      </div>
    </section>
  </Container>
</template>
